import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';
import { UserFormProps } from './userFormSchema';

export type User = {
  created_at: string;
  deleted_at: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  license_expiry_date: string;
  parent_id: string;
  role: string;
  updated_at: string;
};

type UserState = {
  apiErrors: ApiErrors;
  list: {
    [key: string]: User;
  };
  loaders: Loaders;
};

const usersApiPrefix = '/users';

// TODO: use object instead of  array
const initialState: UserState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  list: {},
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  }
};

export const fetchDelegates = createAsyncThunk<User[], null, { rejectValue: string }>(
  'users/fetchDelegates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${usersApiPrefix}/me/delegates`);
      const {
        data: { users }
      } = response;
      return users;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteDelegate = createAsyncThunk<string, string, { rejectValue: string }>(
  'users/deleteDelegate',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id, value: true }));
      await axiosInstance.delete(`${usersApiPrefix}/me/delegates/${id}`);
      return id;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('delegates.you cannot delete this user'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('delegates.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id, value: false }));
    }
  }
);

interface SaveUserParams {
  id?: string;
  user: UserFormProps;
}

export const saveDelegate = createAsyncThunk<User, SaveUserParams, { rejectValue: string }>(
  'saveSite/deleteDelegate',
  async ({ id, user }, { rejectWithValue }) => {
    let response;
    try {
      if (id) {
        response = await axiosInstance.put(`${usersApiPrefix}/me/delegates/${id}`, user);
      } else {
        response = await axiosInstance.post(`${usersApiPrefix}/me/delegates`, user);
      }
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('delegates.you cannot update or create users'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const usersSlice = createSlice({
  initialState,
  name: 'users',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetProductionUsers: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchDelegates.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.list = keyBy(payload, 'id');
      })
      .addCase(fetchDelegates.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchDelegates.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteDelegate.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
      .addCase(saveDelegate.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.list[payload.id] = payload;
      })
      .addCase(saveDelegate.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveDelegate.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = usersSlice;
export { reducer as users };
export const { closeError, resetProductionUsers, updateListDelete } = actions;

export const getUsers = (state: RootState) => state.users.list;
export const getUsersLoaders = (state: RootState) => state.users.loaders;
export const getUsersApiErrors = (state: RootState) => state.users.apiErrors;
